import { AgentServiceError, isContextWindowExceededMessage, parseAgentServiceError } from "./agentErrors";
import type { AgentChatResponse, AgentContextUsage } from "../types/agentChat";

export interface AgentActivityEvent {
  type: string;
  toolName?: string;
  status?: string;
}

export interface AgentSseHandlers {
  onTextDelta?: (delta: string) => void;
  onToolCall?: (name: string) => void;
  /** Live SSE activity (tool calls, run status, composing). Optional. */
  onActivity?: (event: AgentActivityEvent) => void;
}

interface SseDonePayload {
  session_id: string;
  response: string;
  context_usage?: AgentContextUsage | null;
}

interface SseErrorPayload {
  code?: string;
  message?: string;
  retryable?: boolean;
}

interface RunnerActivity {
  type?: string;
  text?: string;
  partial?: boolean;
  toolName?: string;
  message?: string;
  status?: string;
  sessionId?: string;
  token_stats?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
  };
}

function parseSseBlock(block: string): { event: string; data: string } | null {
  const trimmed = block.trim();
  if (!trimmed || trimmed.startsWith(":")) {
    return null;
  }

  let event = "message";
  const dataLines: string[] = [];

  for (const line of trimmed.split("\n")) {
    if (line.startsWith(":")) {
      continue;
    }
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return { event, data: dataLines.join("\n") };
}

function activityType(raw: string | undefined): string {
  return (raw ?? "").trim().toUpperCase();
}

function handleLegacySseEvent(
  event: string,
  data: Record<string, unknown>,
  handlers: AgentSseHandlers,
): AgentChatResponse | void {
  switch (event) {
    case "text_delta": {
      const delta = data.delta;
      if (typeof delta === "string" && delta.length > 0) {
        handlers.onTextDelta?.(delta);
      }
      return;
    }
    case "tool_call": {
      const name = data.name;
      if (typeof name === "string") {
        handlers.onToolCall?.(name);
        handlers.onActivity?.({ type: "TOOL_CALL", toolName: name });
      }
      return;
    }
    case "done": {
      const payload = data as unknown as SseDonePayload;
      return {
        session_id: payload.session_id,
        response: payload.response,
        context_usage: payload.context_usage ?? null,
      };
    }
    case "error": {
      const payload = data as unknown as SseErrorPayload;
      const code = payload.code ?? "unknown";
      const message = payload.message ?? "Agent request failed";
      if (code === "context_window_exceeded") {
        throw new AgentServiceError(message, "context_window_exceeded");
      }
      throw new AgentServiceError(message);
    }
    default:
      return;
  }
}

function tokenStatsToUsage(
  stats: RunnerActivity["token_stats"],
): AgentContextUsage | null {
  if (!stats || typeof stats.total_tokens !== "number") {
    return null;
  }
  return {
    used_tokens: stats.total_tokens,
    limit_tokens: 0,
    usage_percent: 0,
  };
}

/**
 * Read an agent chat SSE response until runner `RUN_STATUS completed` or a legacy `done` event.
 */
export async function consumeAgentSseStream(
  response: Response,
  handlers: AgentSseHandlers,
  errorPrefix: string,
): Promise<AgentChatResponse> {
  if (!response.ok) {
    throw await parseAgentServiceError(response, errorPrefix);
  }

  const body = response.body;
  if (!body) {
    throw new AgentServiceError(`${errorPrefix}: empty response body`);
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: AgentChatResponse | null = null;
  let displayed = "";
  let lastFullText = "";
  let sessionId = "";
  let contextUsage: AgentContextUsage | null = null;
  let runError: string | null = null;

  const emitText = (text: string, partial: boolean) => {
    if (!text) return;
    if (partial) {
      if (text.startsWith(displayed)) {
        const delta = text.slice(displayed.length);
        if (delta) handlers.onTextDelta?.(delta);
        displayed = text;
      } else {
        handlers.onTextDelta?.(text);
        displayed += text;
      }
      return;
    }
    if (text.startsWith(displayed)) {
      const delta = text.slice(displayed.length);
      if (delta) handlers.onTextDelta?.(delta);
    }
    displayed = text;
    lastFullText = text;
  };

  const handleRunnerActivity = (activity: RunnerActivity): AgentChatResponse | void => {
    const type = activityType(activity.type);
    if (type !== "ERROR" && type !== "TEXT") {
      handlers.onActivity?.({
        type,
        toolName: activity.toolName,
        status: activity.status,
      });
    }
    if (type === "TEXT" && activity.text) {
      emitText(activity.text, Boolean(activity.partial));
      return;
    }
    if (type === "TOOL_CALL" && activity.toolName) {
      handlers.onToolCall?.(activity.toolName);
      return;
    }
    if (type === "ERROR") {
      const message = activity.message ?? "Agent request failed";
      if (activity.toolName) {
        return;
      }
      if (isContextWindowExceededMessage(message)) {
        throw new AgentServiceError(
          "This conversation has grown too large for the model. Start a new chat to continue.",
          "context_window_exceeded",
        );
      }
      runError = message;
      return;
    }
    if (type === "RUN_STATUS") {
      if (activity.sessionId) sessionId = activity.sessionId;
      if (activity.token_stats) contextUsage = tokenStatsToUsage(activity.token_stats);
      if (activity.status === "completed") {
        if (runError && !(lastFullText || displayed).trim()) {
          throw new AgentServiceError(runError);
        }
        return {
          session_id: sessionId,
          response: lastFullText || displayed,
          context_usage: contextUsage,
        };
      }
    }
  };

  const dispatchParsed = (parsed: { event: string; data: string }) => {
    const data = JSON.parse(parsed.data) as Record<string, unknown>;
    if (typeof data.type === "string") {
      const eventResult = handleRunnerActivity(data as RunnerActivity);
      if (eventResult) result = eventResult;
      return;
    }
    const eventResult = handleLegacySseEvent(parsed.event, data, handlers);
    if (eventResult) result = eventResult;
  };

  const dispatchBuffer = () => {
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";

    for (const block of blocks) {
      const parsed = parseSseBlock(block);
      if (!parsed) {
        continue;
      }
      dispatchParsed(parsed);
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      dispatchBuffer();
    }

    buffer += decoder.decode();
    dispatchBuffer();

    const trailing = parseSseBlock(buffer);
    if (trailing) {
      dispatchParsed(trailing);
    }
  } finally {
    reader.releaseLock();
  }

  if (!result) {
    if (runError) {
      throw new AgentServiceError(runError);
    }
    if (lastFullText || displayed) {
      return {
        session_id: sessionId,
        response: lastFullText || displayed,
        context_usage: contextUsage,
      };
    }
    throw new AgentServiceError(`${errorPrefix}: stream ended without a done event`);
  }

  return result;
}
